import { useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";

import MediaDialog from "../atoms/MediaDialog";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import FieldLabel from "../atoms/FieldLabel";
import FormTemplate from "../templates/FormTemplate";
import { ReactSketchCanvas, ReactSketchCanvasRef } from "react-sketch-canvas";
import useBreakpoint from "../../hooks/useBreakpoint";
import BorderColorIcon from "@mui/icons-material/BorderColor";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import BackspaceIcon from "@mui/icons-material/Backspace";

interface SignatureInputProps {
  field: string;
  label?: string;
  value: string;
  onChange?: (value: string) => any;
}

const defaultImage = require("../../assets/no-image.jpg");

export default function SignatureInput(props: SignatureInputProps) {
  const breakpoint = useBreakpoint();
  const { t } = useTranslation("SignatureInput");
  const { value, onChange } = props;
  const [mediaDialog, setMediaDialog] = useState(false);
  const [signatureDialog, setSignatureDialog] = useState(false);
  const [eraseMode, setEraseMode] = useState(false);
  const theme = useTheme();
  const canvasRef = useRef<ReactSketchCanvasRef | null>(null);

  const handleClickSign = () => {
    setSignatureDialog(true);
  };

  const handleApplySignature = async () => {
    const base64 = await canvasRef.current?.exportImage("png");
    if (base64) onChange?.(base64);
    handleCloseSignatureDialog();
  };

  const handleCloseSignatureDialog = () => {
    setSignatureDialog(false);
  };

  const handleClear = () => {
    onChange?.("");
  };

  const handleCloseMediaDialog = () => {
    setMediaDialog(false);
  };

  const handleClickImage = () => {
    if (value) setMediaDialog(true);
  };

  const handleEraserClick = () => {
    setEraseMode(true);
    canvasRef.current?.eraseMode(true);
  };

  const handlePenClick = () => {
    setEraseMode(false);
    canvasRef.current?.eraseMode(false);
  };

  useEffect(() => {
    if (signatureDialog) handlePenClick();
  }, [signatureDialog]);

  return (
    <Stack width={1} justifyContent="space-between" direction="row" spacing={1}>
      <Dialog open={signatureDialog} fullScreen={!breakpoint.md}>
        <FormTemplate
          noForm
          title={t("Signature")}
          onSubmit={handleApplySignature}
          onCancel={handleCloseSignatureDialog}
        >
          <Stack spacing={2} height={1}>
            <ReactSketchCanvas
              ref={canvasRef}
              strokeWidth={2}
              strokeColor="#002b59"
              canvasColor="transparent"
              eraserWidth={64}
              style={{
                border: "1px solid",
                borderColor: theme.palette.divider,
                width: breakpoint.md ? 500 : "100%",
                height: breakpoint.md ? 300 : "100%",
              }}
            />
            <Stack direction="row" spacing={2}>
              <Button
                variant={eraseMode ? "outlined" : "contained"}
                onClick={handlePenClick}
              >
                <BorderColorIcon />
              </Button>
              <Button
                variant={eraseMode ? "contained" : "outlined"}
                onClick={handleEraserClick}
              >
                <BackspaceIcon sx={{ rotate: "-45deg" }} />
              </Button>
            </Stack>
          </Stack>
        </FormTemplate>
      </Dialog>
      <Stack flexGrow={1}>
        {props.label && <FieldLabel>{props.label}</FieldLabel>}
        <Button
          variant="outlined"
          startIcon={<BorderColorIcon />}
          onClick={handleClickSign}
          sx={{ width: "fit-content" }}
        >
          {t("Sign")}
        </Button>
      </Stack>

      <Box position="relative">
        {value && (
          <IconButton
            sx={{
              p: 0.25,
              position: "absolute",
              right: 8,
              top: 8,
              zIndex: 1,
              bgcolor: "background.default",
              ":hover": {
                bgcolor: "background.paper",
              },
            }}
            color="error"
            size="small"
            onClick={handleClear}
          >
            <CloseIcon />
          </IconButton>
        )}
        <ButtonBase onClick={handleClickImage} sx={{ borderRadius: 1 }}>
          <img
            src={value || defaultImage}
            style={{
              height: 80,
              width: 80,
              objectFit: "cover",
              borderRadius: "inherit",
            }}
          />
        </ButtonBase>
      </Box>

      <MediaDialog
        filename={t("Signature")}
        open={mediaDialog}
        src={value}
        type="image"
        onClose={handleCloseMediaDialog}
      />
    </Stack>
  );
}
